import React, { Dispatch, ReactElement } from "react";
import { RouteComponentProps, Redirect, Switch } from "react-router";
import { connect } from "react-redux";
import { User } from "oidc-client";
import { ProtectedRoute } from "../../Auth";
import {
  appPaths,
  IAppState,
  LoadingState,
  ApiRequest,
  PageTitleSetter,
  appPageTitles
} from "../../Common";
import { TopicFeedbackContainer } from "./FeedbackLists/TopicFeedbackContainer";
import TopicFeatureRequestsContainer from "./FeedbackLists/TopicFeatureRequestsContainer";
import { setPath, setBreadcrumbs } from "../../Startup/Actions/StartupActions";
import { breadcrumbsMap } from "../Constants/breadcrumbsMap";
import { Topic } from "../Models/Topic";
import { breadcrumbService } from "../../Layout";
import { BreadcrumbData } from "../../Layout/Models/BreadcrumbData";
import { endpoints } from "../Constants/endpoints";
import { endpoints as feedbackEndpoints } from "../../Feedback/Constants/endpoints";
import { ResponseResult } from "../../Common/Models/ResponseResult";
import ResponseHandler from "../../App/ResponseHandler";
import { Feedback } from "../../Feedback";
import ViewFeedbackContainer from "../../Feedback/Components/FeedbackDetails/ViewFeedbackContainer";
import TopicsListContainer from "./TopicsList/TopicsListContainer";
import TopicWorkflowListContainer from "./TopicWorkflows/TopicWorkflowListContainer";
import SubmitFeedbackContainer from "../../Feedback/Components/FeedbackForm/SubmitFeedbackContainer";

type TParams = {
  code: string;
};

type StateProps = {
  path?: string;
  user?: User;
  topics: Topic[];
  topicsLoadingState: LoadingState;
};

type DispatchProps = {
  setPath: (location: string) => void;
  setBreadcrumbs: (breadcrumbs: Promise<BreadcrumbData[]>) => void;
};

type State = {
  pageTitle: string;
  setTitleForUrl: string;
};

const mapStateToProps = (state: IAppState): StateProps => ({
  path: state.app.path,
  topics: state.app.topics,
  topicsLoadingState: state.app.topicsLoadingState,
  user: state.oidc.user
});

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
  setPath: (location: string) => dispatch(setPath(location)),
  setBreadcrumbs: (breadcrumbs: Promise<BreadcrumbData[]>) => dispatch(setBreadcrumbs(breadcrumbs))
});

class TopicRoutes extends React.Component<
  RouteComponentProps<TParams> & StateProps & DispatchProps,
  State
> {
  constructor(props: RouteComponentProps<TParams> & StateProps & DispatchProps) {
    super(props);
    this.state = {
      pageTitle: appPageTitles.topics,
      setTitleForUrl: props.location.pathname
    };
  }

  componentDidUpdate() {
    if (this.state.setTitleForUrl !== this.props.location.pathname) {
      // set the title to the default value
      this.setState((prevState: State) => ({
        pageTitle: appPageTitles.topics,
        setTitleForUrl: this.props.location.pathname
      }));
    }
  }

  getTopicName = async (code?: string) => {
    let name;

    if (code && !!this.props.user) {
      await ApiRequest.fromPath(endpoints.topics.getByCode(code))
        .withAuthHeader()
        .get()
        .then(async response => {
          await ResponseHandler.handle(response, (result: ResponseResult<Topic>) => {
            name = result.payload.name;
            this.setState(() => ({
              pageTitle: result.payload.name
            }));
          });
        });
    }
    return name;
  };

  getFeedbackName = async (code?: string) => {
    let title;
    if (code && this.props.user) {
      await ApiRequest.fromPath(feedbackEndpoints.feedback.getTitle(code))
        .withAuthHeader()
        .get()
        .then(async response => {
          await ResponseHandler.handle(response, (result: ResponseResult<Feedback>) => {
            title = result.payload;
          });
        });
    }

    return title;
  };

  resolveVariable = async (key: string, variables: Map<string, string>) => {
    switch (key) {
      case ":topicCode":
        return this.getTopicName(variables.get(key) || "");
      case ":code":
        return this.getFeedbackName(variables.get(key) || "");
      default:
        return key;
    }
  };

  setBreadcrumbs = () => {
    if (this.props.path !== this.props.location.pathname) {
      // this.props.setPath(this.props.location.pathname);

      this.props.setBreadcrumbs(
        breadcrumbService.createBreadcrumbs(
          this.props.location.pathname,
          breadcrumbsMap,
          this.resolveVariable
        )
      );
    }
  };

  render() {
    this.setBreadcrumbs();

    return (
      <>
        <PageTitleSetter title={this.state.pageTitle} />
        <Switch>
          <ProtectedRoute exact path={appPaths.topic.root} component={TopicsListContainer} />
          <ProtectedRoute
            exact
            path={appPaths.topic.workflowTemplates}
            component={TopicWorkflowListContainer}
          />
          <ProtectedRoute
            exact
            path={appPaths.topic.addFeedback}
            component={SubmitFeedbackContainer}
          />
          <ProtectedRoute
            exact
            path={appPaths.topic.featureRequests}
            component={TopicFeatureRequestsContainer}
          />
          <ProtectedRoute
            exact
            path={appPaths.topic.feedback}
            component={TopicFeatureRequestsContainer}
          />
          <ProtectedRoute
            exact
            path={appPaths.topic.specificRoot}
            component={TopicFeatureRequestsContainer}
          />
          <ProtectedRoute
            exact
            path={appPaths.topic.viewFeedback}
            component={ViewFeedbackContainer}
          />
          <ProtectedRoute
            exact
            path={appPaths.topic.viewFeatureRequest}
            component={ViewFeedbackContainer}
          />
          <ProtectedRoute
            exact
            path={appPaths.topic.viewBugReport}
            component={ViewFeedbackContainer}
          />
          <Redirect to={appPaths.notFound} />
        </Switch>
      </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(TopicRoutes);
